import { db } from '../firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  Timestamp,
} from 'firebase/firestore';
import {
  SpecialAward,
  SpecialAwardWithDetails,
} from '../types/specialAwards.types';

export const specialAwardsService = {
  /**
   * Create a new special award
   */
  async createSpecialAward(
    fairId: string,
    awardName: string,
    projectIds: string[],
    studentIds: string[],
    volunteerId: string
  ): Promise<string> {
    try {
      const now = new Date();
      const awardData = {
        fairId,
        awardName,
        projectIds,
        studentIds,
        createdBy: volunteerId,
        createdAt: Timestamp.fromDate(now),
        updatedAt: Timestamp.fromDate(now),
      };

      const docRef = await addDoc(collection(db, 'specialAwards'), awardData);
      return docRef.id;
    } catch (error) {
      console.error('Error creating special award:', error);
      throw error;
    }
  },

  /**
   * Get all special awards for a fair
   */
  async getSpecialAwardsByFair(fairId: string): Promise<SpecialAward[]> {
    try {
      const awardsRef = collection(db, 'specialAwards');
      const q = query(awardsRef, where('fairId', '==', fairId));
      const snapshot = await getDocs(q);

      return snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          fairId: data.fairId,
          awardName: data.awardName,
          projectIds: data.projectIds || [],
          studentIds: data.studentIds || [],
          createdBy: data.createdBy,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        };
      });
    } catch (error) {
      console.error('Error fetching special awards:', error);
      throw error;
    }
  },

  /**
   * Get special awards with project and student details
   */
  async getSpecialAwardsWithDetails(
    fairId: string
  ): Promise<SpecialAwardWithDetails[]> {
    try {
      const awards = await this.getSpecialAwardsByFair(fairId);
      const awardsWithDetails: SpecialAwardWithDetails[] = [];

      for (const award of awards) {
        // Get project details
        const projects = [];
        for (const projectId of award.projectIds) {
          try {
            const projectDoc = await getDoc(doc(db, 'projects', projectId));
            if (projectDoc.exists()) {
              const projectData = projectDoc.data();
              projects.push({
                id: projectId,
                name: projectData.name || 'Unknown Project',
              });
            }
          } catch (error) {
            console.error(`Error fetching project ${projectId}:`, error);
          }
        }

        // Get student details
        const students = [];
        for (const studentId of award.studentIds) {
          try {
            const userDoc = await getDoc(doc(db, 'users', studentId));
            if (userDoc.exists()) {
              const userData = userDoc.data();
              students.push({
                id: studentId,
                name: `${userData.firstName || ''} ${userData.lastName || ''}`.trim(),
                school: userData.school || '',
              });
            }
          } catch (error) {
            console.error(`Error fetching student ${studentId}:`, error);
          }
        }

        awardsWithDetails.push({
          ...award,
          projects,
          students,
        });
      }

      return awardsWithDetails;
    } catch (error) {
      console.error('Error fetching special awards with details:', error);
      throw error;
    }
  },

  /**
   * Update a special award
   */
  async updateSpecialAward(
    awardId: string,
    awardName: string,
    projectIds: string[],
    studentIds: string[]
  ): Promise<void> {
    try {
      const awardRef = doc(db, 'specialAwards', awardId);
      await updateDoc(awardRef, {
        awardName,
        projectIds,
        studentIds,
        updatedAt: Timestamp.fromDate(new Date()),
      });
    } catch (error) {
      console.error('Error updating special award:', error);
      throw error;
    }
  },

  /**
   * Delete a special award
   */
  async deleteSpecialAward(awardId: string): Promise<void> {
    try {
      const awardRef = doc(db, 'specialAwards', awardId);
      await deleteDoc(awardRef);
    } catch (error) {
      console.error('Error deleting special award:', error);
      throw error;
    }
  },

  /**
   * Get a single special award
   */
  async getSpecialAward(awardId: string): Promise<SpecialAward | null> {
    try {
      const awardRef = doc(db, 'specialAwards', awardId);
      const awardDoc = await getDoc(awardRef);

      if (!awardDoc.exists()) {
        return null;
      }

      const data = awardDoc.data();
      return {
        id: awardDoc.id,
        fairId: data.fairId,
        awardName: data.awardName,
        projectIds: data.projectIds || [],
        studentIds: data.studentIds || [],
        createdBy: data.createdBy,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
      };
    } catch (error) {
      console.error('Error fetching special award:', error);
      throw error;
    }
  },
};
